// Shared rendering for credit-usage breakdowns, used by both the user settings Usage section
// (per-project, expandable) and the project settings Usage section (a single project's task list).
import { BadgeCheck } from 'lucide-react'
import type { ProjectUsageTask } from '../data/accountTypes'

// Badge shown on projects that earned free QA at the project level (sharing program, or accepted
// missing-bug / report-problem feedback). Render only when the project's qa_costs_refunded flag is
// set — NOT merely when some credits were refunded, since a one-off failed-task refund is not free QA.
export function RefundedBadge({ credits }: { credits?: number }) {
  return (
    <span
      className="settings-usage__refunded-badge"
      data-testid="usage-refunded-badge"
      title={credits ? `${formatCredits(credits)} refunded — this project's QA costs were refunded` : "This project's QA costs were refunded"}
    >
      <BadgeCheck size={13} aria-hidden />
      QA costs refunded
    </span>
  )
}

function parseUsageDate(date: string): Date | null {
  // `date` is a full ISO timestamp (e.g. 2026-06-13T18:42:05Z). Older payloads may
  // still send a date-only string; normalize those so they parse as local midnight.
  const parsed = new Date(/\d{2}:\d{2}/.test(date) ? date : `${date}T00:00:00`)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export function formatUsageDate(date: string): string {
  const parsed = parseUsageDate(date)
  if (!parsed) return date
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

// Compact variant for narrow screens (drops the year) so the usage row's columns don't overflow.
export function formatUsageDateShort(date: string): string {
  const parsed = parseUsageDate(date)
  if (!parsed) return date
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function formatCredits(credits: number): string {
  return `${credits.toLocaleString()} ${credits === 1 ? 'credit' : 'credits'}`
}

export const USAGE_TASK_KIND_LABELS: Record<string, string> = {
  exploration: 'Exploration',
  journey: 'Journey',
  task: 'Task',
}

// The per-task credit breakdown: one row per metered task (kind, label, date, net credits).
export function UsageTaskList({ tasks }: { tasks: ProjectUsageTask[] }) {
  return (
    <ul className="settings-usage__entries">
      {tasks.map((task) => (
        <li key={task.task_id} className="settings-usage__entry">
          <span className="settings-usage__entry-kind">
            {USAGE_TASK_KIND_LABELS[task.kind] ?? 'Task'}
          </span>
          <span className="settings-usage__entry-label" title={task.label}>
            {task.label}
          </span>
          <span className="settings-usage__entry-date">
            <span className="settings-usage__entry-date-full">{formatUsageDate(task.date)}</span>
            <span className="settings-usage__entry-date-short">{formatUsageDateShort(task.date)}</span>
          </span>
          <span className="settings-usage__entry-credits">{formatCredits(task.credits)}</span>
        </li>
      ))}
    </ul>
  )
}
